import { AppThemeProvider } from "@/context/AppThemeContext";
import * as Sentry from "@sentry/react";
import { Replay } from "@sentry/react";
import { createBrowserHistory } from "history";
import _ from "lodash";
import moment from "moment";
import "ol/ol.css";
import { Suspense } from "react";
import { CookiesProvider } from "react-cookie";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { createRoot } from "react-dom/client";
import { I18nextProvider } from "react-i18next";
import { Provider } from "react-redux";
import { Router } from "react-router-dom";
import { CustomProvider } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import App from "./App";
import ErrorBoundaryErrorPage from "./components/AppErrorBoundary/ErrorBoundayErrorPage";
import LoadMask from "./components/LoadMask/LoadMask";
import Log from "./debug/Log";
import i18n, { i18nOptions } from "./i18n";
import "./index.scss";
import init from "./polyfill";
import { store } from "./redux/store";
import * as serviceWorker from "./serviceWorker";
import AssetService from "./services/AssetService";
import CDNService from "./services/CDNService";
import CacheService from "./services/CacheService";
import GroupService from "./services/GroupService";
import IndexDBService from "./services/IndexDBService";
import MandatorService from "./services/MandatorService";
import SubmitService from "./services/SubmitService";
import UserService from "./services/UserService";
import "./styles/custom-rsuite.scss";
import { ComponentsMapper } from "./utils/ComponentsMapper";

// Polyfills
require("polyfill-object.fromentries");

// Global window assignments
(window as any)._ = _;
(window as any).moment = moment;

// Create history instance at module level
const history = createBrowserHistory();

// Initialize services
const initializeServices = () => {
  try {
    ComponentsMapper.init();
    CDNService.init();
    SubmitService.init();
    GroupService.init();
    UserService.init();
    CacheService.init();
    MandatorService.init();
    AssetService.init();
    Log.info("All services initialized successfully");
  } catch (error) {
    Log.error("Failed to initialize services:", error);
  }
};

// Initialize Sentry
const initializeSentry = () => {
  if ((process.env.REACT_APP_STAGE || "local") !== "local") {
    try {
      Sentry.init({
        dsn: "https://8f07ea634e654e9c86e9e1d05f007e87@o4504820443119616.ingest.sentry.io/4504820444299269",
        integrations: [
          new Replay({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
        // ignoreErrors: [
        //   "Loading chunk",
        //   "startCleanup: Page",
        //   "CKEditor",
        //   "Network error",
        //   /ResizeObserver loop completed with undelivered notifications/g,
        //   /Cannot read properties of null \(reading 'model'\)/g,
        //   /'insertBefore' on 'Node'/g,
        //   /'removeChild' on 'Node'/g,
        // ],
        beforeSend: (event, hint) => {
          console.log("SENTRY BEFORESEND", event, hint);
          return event;
        },
        environment: process.env.REACT_APP_STAGE || "local",
        tracesSampleRate: Number(
          process.env.REACT_APP_SENTRY_TRACE_SAMPLE_RATE || 1.0
        ),
        replaysSessionSampleRate: Number(
          process.env.REACT_APP_SENTRY_REPLAY_SESSION_SAMPLE_RATE || 1.0
        ),
        replaysOnErrorSampleRate: Number(
          process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || 1.0
        ),
      });
      Log.info("Sentry initialized successfully");
    } catch (error) {
      Log.error("Failed to initialize Sentry:", error);
    }
  }
};

// Initialize polyfills and services
init();
initializeServices();
initializeSentry();

// Initialize i18n and render app
i18n
  .init(i18nOptions)
  .then(() => {
    Log.info("i18n initialized successfully");

    return IndexDBService.initShared();
  })
  .then(() => {
    Log.info("IndexDB initialized successfully");

    const container = document.getElementById("root");
    if (!container) {
      throw new Error("Root element not found");
    }

    const root = createRoot(container);
    root.render(
      <CookiesProvider>
        <DndProvider
          backend={HTML5Backend}
          options={{ enableMouseEvents: true }}
        >
          <Provider store={store}>
            <I18nextProvider i18n={i18n}>
              <Sentry.ErrorBoundary
                onError={(err) => {
                  Log.info("error boundary", err);
                }}
                fallback={(errorData: {
                  error: Error;
                  componentStack: string;
                  eventId: string;
                  resetError(): void;
                }) => {
                  Log.info("errordata", errorData);
                  return <ErrorBoundaryErrorPage errorData={errorData} />;
                }}
                showDialog={false}
              >
                <Router history={history}>
                  <CustomProvider>
                    <AppThemeProvider>
                      <Suspense fallback={<LoadMask />}>
                        <App />
                      </Suspense>
                    </AppThemeProvider>
                  </CustomProvider>
                </Router>
              </Sentry.ErrorBoundary>
            </I18nextProvider>
          </Provider>
        </DndProvider>
      </CookiesProvider>
    );

    Log.info("App rendered successfully");
  })
  .catch((error) => {
    Log.error("Failed to initialize app:", error);

    // Show user-friendly error message
    const container = document.getElementById("root");
    if (container) {
      container.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
          font-family: Arial, sans-serif;
          text-align: center;
          padding: 20px;
        ">
          <h1>Application Error</h1>
          <p>Sorry, the application failed to start. Please refresh the page or contact support.</p>
          <button onclick="window.location.reload()" style="
            padding: 10px 20px;
            background: #007bff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
          ">
            Refresh Page
          </button>
        </div>
      `;
    }
  });

// Unregister service worker
serviceWorker.unregister();
